import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PasswordInput from '../components/ui/PasswordInput';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import api from '../services/api';

// Two-step reset flow matching the backend:
//   1. POST /auth/password-reset-request  → email lands in inbox with a code
//   2. POST /auth/password-reset-confirm  → submit email + code + new password
//
// Step 2 fields use the shared PasswordInput so users can toggle visibility on
// the new password + confirmation. PasswordInput hides again on blur for safety.

const ForgotPassword = () => {
  const navigate = useNavigate();

  // Step 1 → 2 toggle
  const [step, setStep] = useState(1);

  // Common
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // Step 2
  const [code, setCode]                     = useState('');
  const [newPassword, setNewPassword]       = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email.');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/password-reset-request', { email: email.trim() });
      toast.success(res.data?.detail || 'If that email is registered, a reset code has been sent.');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reset code.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!code.trim())                       { toast.error('Enter the code from your email.'); return; }
    if (!newPassword)                       { toast.error('Enter a new password.');            return; }
    if (newPassword !== confirmPassword)    { toast.error('Passwords do not match.');         return; }
    setLoading(true);
    try {
      await api.post('/auth/password-reset-confirm', {
        email:        email.trim(),
        code:         code.trim(),
        new_password: newPassword,
      });
      toast.success('Password reset successfully. Please sign in.');
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Link
        to="/login"
        className="absolute top-6 left-6 flex items-center gap-2 text-slate-600 hover:text-primary font-jakarta text-sm transition-colors duration-200"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Sign In
      </Link>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-outfit font-bold text-slate-900 mb-2">Kaizen Events</h1>
          <p className="text-slate-600 font-jakarta">Forgot your password?</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-card p-8">
          {step === 1 ? (
            <>
              <h2 className="text-2xl font-outfit font-semibold text-slate-900 mb-2">Reset Password</h2>
              <p className="text-sm text-slate-500 mb-6">
                Enter the email tied to your account. We'll send you a one-time code.
              </p>

              <form onSubmit={handleRequestCode} className="space-y-5">
                <div>
                  <Label htmlFor="fp-email" className="text-slate-700 font-jakarta font-medium mb-2 block">
                    Email Address
                  </Label>
                  <Input
                    id="fp-email"
                    type="email"
                    placeholder="admin@kaizenevents.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    required
                    className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta font-medium rounded-lg shadow-lg shadow-primary/20 transition-all duration-200 active:scale-95"
                >
                  {loading ? 'Sending…' : 'Send Reset Code'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-outfit font-semibold text-slate-900 mb-2">Enter Code</h2>
              <p className="text-sm text-slate-500 mb-6">
                We sent a code to <span className="font-medium text-slate-700">{email}</span>. Enter it below
                along with your new password. (Code is valid for 24 hours.)
              </p>

              <form onSubmit={handleConfirm} className="space-y-5">
                <div>
                  <Label htmlFor="fp-code" className="text-slate-700 font-jakarta font-medium mb-2 block">
                    Reset Code
                  </Label>
                  <Input
                    id="fp-code"
                    type="text"
                    inputMode="numeric"
                    placeholder="6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    autoFocus
                    required
                    className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary tracking-widest"
                  />
                </div>

                <div>
                  <Label htmlFor="fp-new" className="text-slate-700 font-jakarta font-medium mb-2 block">
                    New Password
                  </Label>
                  <PasswordInput
                    id="fp-new"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    At least 12 characters with upper, lower, digit, and special.
                  </p>
                </div>

                <div>
                  <Label htmlFor="fp-confirm" className="text-slate-700 font-jakarta font-medium mb-2 block">
                    Confirm New Password
                  </Label>
                  <PasswordInput
                    id="fp-confirm"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="h-11 rounded-lg border-slate-200 focus-visible:ring-primary"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                    disabled={loading}
                    className="flex-1 h-11 rounded-lg font-jakarta font-medium"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 h-11 bg-primary hover:bg-primary-hover text-primary-foreground font-jakarta font-medium rounded-lg shadow-lg shadow-primary/20 transition-all duration-200 active:scale-95"
                  >
                    {loading ? 'Resetting…' : 'Reset Password'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
